import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import Button from '../ui/button/Button';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  confirmButtonClass?: string;
  cancelButtonClass?: string;
  confirmLoading?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  onConfirm,
  confirmButtonClass = 'bg-primary hover:bg-primary-dark text-white',
  cancelButtonClass = '',
  confirmLoading = false
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const sizeClass = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl'
  }[size];

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleModalContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (!isOpen) return null;

  // Se temos onConfirm e não temos footer personalizado, usamos o footer padrão
  const defaultFooter = onConfirm && !footer ? (
    <div className="flex justify-between space-x-3">
      <Button
        variant="outline"
        size="md"
        onClick={onClose}
        className={cancelButtonClass}
      >
        {cancelText}
      </Button>
      <Button
        variant="primary"
        size="md"
        className={confirmButtonClass}
        onClick={onConfirm}
        disabled={confirmLoading}
      >
        {confirmLoading ? "Carregando..." : confirmText}
      </Button>
    </div>
  ) : null;

  return (
    <div
      className="fixed inset-0 z-[999999] flex items-center justify-center overflow-hidden bg-black/50"
      onClick={onClose}
    >
      <div
        ref={modalRef}
        className={`w-full ${sizeClass} rounded-lg bg-white shadow-xl dark:bg-gray-800 shadow-xl`}
        onClick={handleModalContentClick}
      >
        {title && (
          <header className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{title}</h3>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500 dark:hover:bg-gray-700"
            >
              <X className="h-5 w-5" />
            </button>
          </header>
        )}

        <main className="px-6 py-4">{children}</main>

        {(footer || defaultFooter) && (
          <footer className="border-t border-gray-200 px-6 py-4 dark:border-gray-700">
            {footer || defaultFooter}
          </footer>
        )}
      </div>
    </div>
  );
};

export default Modal;
